// src/pages/Dashboard/Dashboard.tsx

import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import "./Dashboard.css";
import { getDashboardData } from "../../services/dashboardService";
import { RootState } from "../../redux/store";
import {
  DashboardMetrics,
  DashboardGrowth,
  RecentTransaction,
  UserProfile,
} from "../../types/dashboard";
import PageLoader from "../../compoonents/PageLoader";

// --- ICON IMPORTS ---
import totalShipmentsIcon from "../../assets/images/total-shipment.png";
import totalCustomersIcon from "../../assets/images/customers.png";
import completedShipmentsIcon from "../../assets/images/completedShipment.png";
import transactionCardIcon from "../../assets/images/total-transactions.png";
import shop4meCardIcon from "../../assets/images/shop4me.png";
import statusCompletedIcon from "../../assets/images/completedicon.png";
import statusPendingIcon from "../../assets/images/pendingicon.png";
import statusFailedIcon from "../../assets/images/failedicon.png";

type Currency = "ngn" | "gbp";

interface DashboardData {
  metrics: DashboardMetrics;
  growth: DashboardGrowth;
  profile: UserProfile | null;
}

const Dashboard: React.FC = () => {
  const authUser = useSelector((state: RootState) => state.auth.user);
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [activeCurrency, setActiveCurrency] = useState<Currency>("ngn");
  const [activeTxFilter, setActiveTxFilter] = useState("all");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getDashboardData();
        setDashboardData(data);
      } catch (err) {
        console.error("Dashboard fetch failed:", err);
        setError("Failed to load dashboard data. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatCurrency = (amount: number, currency: "NGN" | "GBP") => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(amount);
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case "SUCCESSFUL":
        return statusCompletedIcon;
      case "PENDING":
        return statusPendingIcon;
      case "FAILED":
        return statusFailedIcon;
      default:
        return statusPendingIcon;
    }
  };

  if (loading) {
    return <PageLoader message="Loading dashboard..." />;
  }

  if (error || !dashboardData) {
    return (
      <div className="dashboard-error">
        {error || "An unknown error occurred."}
      </div>
    );
  }

  const filteredTransactions =
    dashboardData.metrics?.recentTransactions?.filter((tx) => {
      if (activeTxFilter === "all") return true;
      return tx.status.toLowerCase() === activeTxFilter;
    }) || []; // Use optional chaining here as well for safety

  // === THE FIX IS HERE ===
  // We use optional chaining `?.` to safely access nested properties.
  // We also provide a fallback name 'Admin' in case the profile data is missing.
  const welcomeName =
    dashboardData?.profile?.first_name ||
    authUser?.name?.split(/\s+/)[0] ||
    "Admin";

  return (
    <div className="dashboard-page">
      <div className="dashboard-welcome">
        <h2>
          <span style={{ fontWeight: 10 }}>Welcome</span>, {welcomeName}
        </h2>
      </div>

      <div className="stats-grid">
        {/* Card 1: Total Shipments */}
        <div className="stat-card card-blue">
          <span className="percentage-badge">
            {dashboardData.growth.shipmentsGrowth}%
          </span>
          <div className="card-icon-bg">
            <img src={totalShipmentsIcon} alt="" />
          </div>
          <div className="card-text-content">
            <p className="card-title">Total Shipments</p>
            <div className="card-value-row">
              <h3 className="card-value">
                {dashboardData.metrics.totalShipments}
              </h3>
            </div>
          </div>
        </div>

        {/* Card 2: Total Customers */}
        <div className="stat-card card-orange">
          <span className="percentage-badge">
            {dashboardData.growth.customersGrowth}%
          </span>
          <div className="card-icon-bg">
            <img src={totalCustomersIcon} alt="" />
          </div>
          <div className="card-text-content">
            <p className="card-title">Total Customers</p>
            <div className="card-value-row">
              <h3 className="card-value">
                {dashboardData.metrics.totalCustomers}
              </h3>
            </div>
          </div>
        </div>

        {/* Card 3: Completed Shipments */}
        <div className="stat-card card-blue">
          <span className="percentage-badge">
            {dashboardData.growth.completedShipmentsGrowth}%
          </span>
          <div className="card-icon-bg">
            <img src={completedShipmentsIcon} alt="" />
          </div>
          <div className="card-text-content">
            <p className="card-title">Completed Shipments</p>
            <div className="card-value-row">
              <h3 className="card-value">
                {dashboardData.metrics.completedShipments}
              </h3>
            </div>
          </div>
        </div>

        {/* Transaction card's structure is already good for mobile */}
        <div className="stat-card card-orange transaction-card">
          <div className="transaction-card-header">
            <div className="currency-toggle">
              <button
                className={activeCurrency === "ngn" ? "active" : ""}
                onClick={() => setActiveCurrency("ngn")}
              >
                NGN
              </button>
              <button
                className={activeCurrency === "gbp" ? "active" : ""}
                onClick={() => setActiveCurrency("gbp")}
              >
                GBP
              </button>
            </div>
            <div className="card-icon-bg">
              <img src={transactionCardIcon} alt="transaction" />
            </div>
          </div>
          <p className="card-title">Total Transaction</p>
          <h3 className="card-value">
            {activeCurrency === "ngn"
              ? formatCurrency(
                  dashboardData.metrics.totalTransactionsNGN,
                  "NGN"
                )
              : formatCurrency(
                  dashboardData.metrics.totalTransactionsGBP,
                  "GBP"
                )}
          </h3>
        </div>
      </div>
      <div className="main-content-grid">
        <div className="recent-transactions-card">
          <div className="card-header">
            <h4>Recent Shipment Transaction</h4>
            <Link to="/transactions" className="see-all-link">
              See all
            </Link>
          </div>
          <div className="transactions-tabs">
            <button
              className={activeTxFilter === "all" ? "active" : ""}
              onClick={() => setActiveTxFilter("all")}
            >
              All
            </button>
            <button
              className={activeTxFilter === "pending" ? "active" : ""}
              onClick={() => setActiveTxFilter("pending")}
            >
              Pending
            </button>
            <button
              className={activeTxFilter === "failed" ? "active" : ""}
              onClick={() => setActiveTxFilter("failed")}
            >
              Failed
            </button>
            <button
              className={activeTxFilter === "successful" ? "active" : ""}
              onClick={() => setActiveTxFilter("successful")}
            >
              Completed
            </button>
          </div>
          <ul className="transactions-list">
            {filteredTransactions.map((tx: RecentTransaction) => (
              <li key={tx.id}>
                <div className={`tx-status-icon ${tx.status.toLowerCase()}`}>
                  <img src={getStatusIcon(tx.status)} alt={tx.status} />
                </div>
                <div className="tx-details">
                  <p className="tx-description">{`Payment from ${tx.user?.firstName} ${tx.user?.lastName}`}</p>
                  <p className="tx-id">{tx.id}</p>
                </div>
                <div className="tx-info">
                  <p className="tx-date">
                    {new Date(tx.createdAt).toLocaleDateString()}
                  </p>
                  <p className="tx-time">
                    {new Date(tx.createdAt).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
                <div className="tx-amount">
                  {formatCurrency(tx.amount, tx.currency)}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="shop4me-card">
          <div className="card-icon-bg">
            <img src={shop4meCardIcon} alt="shop for me" />
          </div>
          <p className="card-title">
            Total <br />
            Shop4me Request
          </p>
          <h3 className="card-value">
            {dashboardData.metrics.totalShopForMeRequests}
          </h3>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
